import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import type {
  DeleteAdminGlobalServiceCategory,
  DeleteAdminIndustry,
  GetAdminGlobalServiceCategories,
  GetAdminGlobalServiceCategoryImageUploadUrl,
  GetAdminIndustries,
  GetAdminIndustryImageUploadUrl,
  GetAdminMarketplaceAccount,
  GetAdminMarketplaceAccounts,
  GetAdminMarketplaceInsightsBookingShare,
  GetAdminMarketplaceInsightsBusinesses,
  GetAdminMarketplaceInsightsGeography,
  GetAdminMarketplaceInsightsLifecycle,
  GetAdminMarketplaceInsightsListings,
  GetAdminMarketplaceInsightsModes,
  GetAdminMarketplaceInsightsOverview,
  GetAdminMarketplaceInsightsOverviewDaily,
  GetAdminMarketplaceInsightsPush,
  GetAdminMarketplaceInsightsPushDaily,
  GetAdminMarketplaceInsightsRatings,
  GetAdminMarketplaceInsightsRatingsDaily,
  GetAdminMarketplaceInsightsRepeatBooking,
  GetAdminMarketplaceInsightsRevenue,
  GetAdminMarketplaceInsightsSalonValue,
  GetAdminMarketplaceInsightsSetupGaps,
  GetAdminMarketplaceInsightsSignupCohorts,
  GetAdminMarketplaceInsightsSignups,
  GetAdminMarketplaceInsightsVerificationSms,
  GetMarketplaceAcquisitions,
  GetRecurrentBillingPreview,
  PostAdminGlobalServiceCategory,
  PostAdminGlobalServiceCategoryImageComplete,
  PostAdminIndustry,
  PostAdminIndustryImageComplete,
  PostAdminMarketplaceAccountResync,
  PostAdminMarketplaceAccountVerifyEmail,
  PostAdminMarketplaceAccountVerifyPhone,
  PostVoidMarketplaceAcquisitions,
  PutAdminGlobalServiceCategory,
  PutAdminIndustry,
} from "@/server-types";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { del, get, post, put } from "../request-helpers";

const INDUSTRIES_QUERY_KEY = ["limeAdminMarketplaceIndustries"];

export const useGetAdminIndustries = (props?: { disabled?: boolean }) => {
  return useCustomQuery({
    queryKey: INDUSTRIES_QUERY_KEY,
    queryFn: () => get<GetAdminIndustries>(`/lime-admin/industry`),
    enabled: !props?.disabled,
  });
};

export const usePostAdminIndustry = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: { body: PostAdminIndustry["body"] }) =>
      post<PostAdminIndustry>(`/lime-admin/industry`, props.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INDUSTRIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["industries", "dashboard"] });
    },
  });
};

export const usePutAdminIndustry = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: {
      industryId: number;
      body: PutAdminIndustry["body"];
    }) =>
      put<PutAdminIndustry>(
        `/lime-admin/industry/${props.industryId}`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INDUSTRIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["industries", "dashboard"] });
    },
  });
};

/**
 * Mint a presigned S3 PUT URL for an industry image. The client uploads the
 * file straight to S3 (see `uploadFileWithXHR`), then confirms via
 * `useCompleteAdminIndustryImageUpload`.
 */
export const useGetAdminIndustryImageUploadUrl = () => {
  return useCustomMutation({
    mutationFn: ({
      industryId,
      query,
    }: {
      industryId: number;
      query: GetAdminIndustryImageUploadUrl["query"];
    }) =>
      get<GetAdminIndustryImageUploadUrl>(
        `/lime-admin/industry/${industryId}/image/upload-url`,
        query,
      ),
  });
};

/**
 * Confirm a direct-to-S3 industry image upload: the server validates and
 * optimizes the object, then records it. Call with `isSuccess: false` to let
 * the server clean up an orphaned object after a failed S3 PUT.
 */
export const useCompleteAdminIndustryImageUpload = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: ({
      industryId,
      body,
    }: {
      industryId: number;
      body: PostAdminIndustryImageComplete["body"];
    }) =>
      post<PostAdminIndustryImageComplete>(
        `/lime-admin/industry/${industryId}/image/complete`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INDUSTRIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["industries", "dashboard"] });
    },
  });
};

export const useDeleteAdminIndustry = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: { industryId: number }) =>
      del<DeleteAdminIndustry>(`/lime-admin/industry/${props.industryId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INDUSTRIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["industries", "dashboard"] });
    },
  });
};

const SERVICE_CATEGORIES_QUERY_KEY = ["limeAdminMarketplaceServiceCategories"];

export const useGetAdminGlobalServiceCategories = (props?: {
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: SERVICE_CATEGORIES_QUERY_KEY,
    queryFn: () =>
      get<GetAdminGlobalServiceCategories>(
        `/lime-admin/global-service-category`,
      ),
    enabled: !props?.disabled,
  });
};

export const usePostAdminGlobalServiceCategory = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: { body: PostAdminGlobalServiceCategory["body"] }) =>
      post<PostAdminGlobalServiceCategory>(
        `/lime-admin/global-service-category`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: SERVICE_CATEGORIES_QUERY_KEY,
      });
    },
  });
};

export const usePutAdminGlobalServiceCategory = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: {
      globalServiceCategoryId: number;
      body: PutAdminGlobalServiceCategory["body"];
    }) =>
      put<PutAdminGlobalServiceCategory>(
        `/lime-admin/global-service-category/${props.globalServiceCategoryId}`,
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: SERVICE_CATEGORIES_QUERY_KEY,
      });
    },
  });
};

/**
 * Mint a presigned S3 PUT URL for a service-category image. The client
 * uploads the file straight to S3 (see `uploadFileWithXHR`), then confirms
 * via `useCompleteAdminGlobalServiceCategoryImageUpload`.
 */
export const useGetAdminGlobalServiceCategoryImageUploadUrl = () => {
  return useCustomMutation({
    mutationFn: ({
      globalServiceCategoryId,
      query,
    }: {
      globalServiceCategoryId: number;
      query: GetAdminGlobalServiceCategoryImageUploadUrl["query"];
    }) =>
      get<GetAdminGlobalServiceCategoryImageUploadUrl>(
        `/lime-admin/global-service-category/${globalServiceCategoryId}/image/upload-url`,
        query,
      ),
  });
};

/**
 * Confirm a direct-to-S3 service-category image upload: the server validates
 * and optimizes the object, then records it. Call with `isSuccess: false` to
 * let the server clean up an orphaned object after a failed S3 PUT.
 */
export const useCompleteAdminGlobalServiceCategoryImageUpload = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: ({
      globalServiceCategoryId,
      body,
    }: {
      globalServiceCategoryId: number;
      body: PostAdminGlobalServiceCategoryImageComplete["body"];
    }) =>
      post<PostAdminGlobalServiceCategoryImageComplete>(
        `/lime-admin/global-service-category/${globalServiceCategoryId}/image/complete`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: SERVICE_CATEGORIES_QUERY_KEY,
      });
    },
  });
};

export const useDeleteAdminGlobalServiceCategory = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: { globalServiceCategoryId: number }) =>
      del<DeleteAdminGlobalServiceCategory>(
        `/lime-admin/global-service-category/${props.globalServiceCategoryId}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: SERVICE_CATEGORIES_QUERY_KEY,
      });
    },
  });
};

const MARKETPLACE_ACCOUNTS_QUERY_KEY = ["limeAdminMarketplaceAccounts"];

export const useGetAdminMarketplaceAccounts = (
  query: GetAdminMarketplaceAccounts["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_ACCOUNTS_QUERY_KEY, query],
    queryFn: () =>
      get<GetAdminMarketplaceAccounts>(
        `/lime-admin/marketplace/accounts`,
        query,
      ),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

export const useGetAdminMarketplaceAccount = (props: {
  globalCustomerId: number;
}) => {
  return useCustomQuery({
    queryKey: [
      ...MARKETPLACE_ACCOUNTS_QUERY_KEY,
      "detail",
      props.globalCustomerId,
    ],
    queryFn: () =>
      get<GetAdminMarketplaceAccount>(
        `/lime-admin/marketplace/accounts/${props.globalCustomerId}`,
      ),
  });
};

export const usePostAdminMarketplaceAccountVerifyEmail = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: { globalCustomerId: number }) =>
      post<PostAdminMarketplaceAccountVerifyEmail>(
        `/lime-admin/marketplace/accounts/${props.globalCustomerId}/verify-email`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MARKETPLACE_ACCOUNTS_QUERY_KEY,
      });
    },
  });
};

export const usePostAdminMarketplaceAccountVerifyPhone = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: { globalCustomerId: number }) =>
      post<PostAdminMarketplaceAccountVerifyPhone>(
        `/lime-admin/marketplace/accounts/${props.globalCustomerId}/verify-phone`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MARKETPLACE_ACCOUNTS_QUERY_KEY,
      });
    },
  });
};

export const usePostAdminMarketplaceAccountResync = () => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: { globalCustomerId: number }) =>
      post<PostAdminMarketplaceAccountResync>(
        `/lime-admin/marketplace/accounts/${props.globalCustomerId}/resync`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MARKETPLACE_ACCOUNTS_QUERY_KEY,
      });
    },
  });
};

export const RECURRENT_BILLING_PREVIEW_QUERY_KEY = [
  "limeAdminRecurrentBillingPreview",
];

const ACQUISITIONS_QUERY_KEY = ["limeAdminMarketplaceAcquisitions"];

export const useGetAdminMarketplaceAcquisitions = (
  query: GetMarketplaceAcquisitions["query"],
  props?: { disabled?: boolean },
) => {
  return useCustomQuery({
    queryKey: [...ACQUISITIONS_QUERY_KEY, query],
    queryFn: () =>
      get<GetMarketplaceAcquisitions>(
        `/lime-admin/marketplace-acquisition`,
        query,
      ),
    enabled: !props?.disabled,
    placeholderData: keepPreviousData,
  });
};

/**
 * Retires acquisitions so the billing sweep never bills them. Irreversible, and
 * only `unbilled` rows are affected — ids already invoiced come back as
 * `skipped`, so the caller has to report both counts.
 */
export const usePostVoidMarketplaceAcquisitions = (options?: {
  onSuccess?: (data: PostVoidMarketplaceAcquisitions["response"]) => void;
}) => {
  const queryClient = useQueryClient();
  return useCustomMutation({
    mutationFn: (props: { body: PostVoidMarketplaceAcquisitions["body"] }) =>
      post<PostVoidMarketplaceAcquisitions>(
        `/lime-admin/marketplace-acquisition/void`,
        props.body,
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ACQUISITIONS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: RECURRENT_BILLING_PREVIEW_QUERY_KEY,
      });
      options?.onSuccess?.(data);
    },
  });
};

export const useGetRecurrentBillingPreview = (props?: {
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: RECURRENT_BILLING_PREVIEW_QUERY_KEY,
    queryFn: () =>
      get<GetRecurrentBillingPreview>(
        `/lime-admin/billing/recurrent-billing-preview`,
      ),
    enabled: !props?.disabled,
    refetchOnWindowFocus: false,
  });
};

const MARKETPLACE_INSIGHTS_QUERY_KEY = ["limeAdminMarketplaceInsights"];

const insightQueryOptions = {
  placeholderData: keepPreviousData,
  refetchOnWindowFocus: false,
  refetchInterval: false,
} as const;

export const useGetAdminMarketplaceInsightsOverview = (
  query: GetAdminMarketplaceInsightsOverview["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "overview", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsOverview>(
        `/lime-admin/marketplace/insights/overview`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsBookingShare = (
  query: GetAdminMarketplaceInsightsBookingShare["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "bookingShare", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsBookingShare>(
        `/lime-admin/marketplace/insights/booking-share`,
        query,
      ),
    ...insightQueryOptions,
    // Counts every booking on the platform, so a revisit reuses the last answer.
    staleTime: 10 * 60_000,
  });
};

export const useGetAdminMarketplaceInsightsBusinesses = (
  query: GetAdminMarketplaceInsightsBusinesses["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "businesses", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsBusinesses>(
        `/lime-admin/marketplace/insights/businesses`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsModes = (
  query: GetAdminMarketplaceInsightsModes["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "modes", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsModes>(
        `/lime-admin/marketplace/insights/modes`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsListings = (
  query: GetAdminMarketplaceInsightsListings["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "listings", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsListings>(
        `/lime-admin/marketplace/insights/listings`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsSignups = (
  query: GetAdminMarketplaceInsightsSignups["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "signups", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsSignups>(
        `/lime-admin/marketplace/insights/signups`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsSignupCohorts = (
  query: GetAdminMarketplaceInsightsSignupCohorts["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "signup-cohorts", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsSignupCohorts>(
        `/lime-admin/marketplace/insights/signup-cohorts`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsRevenue = (
  query: GetAdminMarketplaceInsightsRevenue["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "revenue", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsRevenue>(
        `/lime-admin/marketplace/insights/revenue`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsVerificationSms = (
  query: GetAdminMarketplaceInsightsVerificationSms["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "verification-sms", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsVerificationSms>(
        `/lime-admin/marketplace/insights/verification-sms`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsSalonValue = (
  query: GetAdminMarketplaceInsightsSalonValue["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "salon-value", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsSalonValue>(
        `/lime-admin/marketplace/insights/salon-value`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsRepeatBooking = (
  query: GetAdminMarketplaceInsightsRepeatBooking["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "repeat-booking", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsRepeatBooking>(
        `/lime-admin/marketplace/insights/repeat-booking`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useExportAdminMarketplaceInsightsBusinesses = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationFn: async (
      query: Omit<
        GetAdminMarketplaceInsightsBusinesses["query"],
        "page" | "perPage"
      >,
    ) => {
      const businesses: GetAdminMarketplaceInsightsBusinesses["response"]["businesses"] =
        [];
      for (let page = 1; ; page++) {
        const response = await get<GetAdminMarketplaceInsightsBusinesses>(
          `/lime-admin/marketplace/insights/businesses`,
          { ...query, page, perPage: 100 },
        );
        businesses.push(...response.businesses);
        if (page >= response.totalPages) return businesses;
      }
    },
  });
};

export const useExportAdminMarketplaceInsightsOverviewDaily = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationFn: (query: GetAdminMarketplaceInsightsOverviewDaily["query"]) =>
      get<GetAdminMarketplaceInsightsOverviewDaily>(
        `/lime-admin/marketplace/insights/overview/daily`,
        query,
      ),
  });
};

export const useExportAdminMarketplaceInsightsRatingsDaily = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationFn: (query: GetAdminMarketplaceInsightsRatingsDaily["query"]) =>
      get<GetAdminMarketplaceInsightsRatingsDaily>(
        `/lime-admin/marketplace/insights/ratings/daily`,
        query,
      ),
  });
};

export const useExportAdminMarketplaceInsightsPushDaily = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationFn: (query: GetAdminMarketplaceInsightsPushDaily["query"]) =>
      get<GetAdminMarketplaceInsightsPushDaily>(
        `/lime-admin/marketplace/insights/push/daily`,
        query,
      ),
  });
};

export const useGetAdminMarketplaceInsightsSetupGaps = (
  query: GetAdminMarketplaceInsightsSetupGaps["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "setupGaps", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsSetupGaps>(
        `/lime-admin/marketplace/insights/setup-gaps`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsRatings = (
  query: GetAdminMarketplaceInsightsRatings["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "ratings", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsRatings>(
        `/lime-admin/marketplace/insights/ratings`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsGeography = (
  query: GetAdminMarketplaceInsightsGeography["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "geography", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsGeography>(
        `/lime-admin/marketplace/insights/geography`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsPush = (
  query: GetAdminMarketplaceInsightsPush["query"],
) => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "push", query],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsPush>(
        `/lime-admin/marketplace/insights/push`,
        query,
      ),
    ...insightQueryOptions,
  });
};

export const useGetAdminMarketplaceInsightsLifecycle = () => {
  return useCustomQuery({
    queryKey: [...MARKETPLACE_INSIGHTS_QUERY_KEY, "lifecycle"],
    queryFn: () =>
      get<GetAdminMarketplaceInsightsLifecycle>(
        `/lime-admin/marketplace/insights/lifecycle`,
      ),
    ...insightQueryOptions,
  });
};
